<template>
  <div class="wt2-auto-complete -autocomplete" v-click-outside="setBlur">
    <div class="wt-input-holder wt2-text-l" :class="inputClasses">
      <input
        ref="input"
        type="text"
        :value="tempValue"
        :placeholder="placeholder"
        :class="inputClasses"
        @input="onInput($event.target.value)"
        @mousedown="touched = true"
        @keydown="touched = true"
        :disabled="disableInput"
        @focus="
          setFocus();
          onInput($event.target.value);
        "
      />
    </div>

    <div class="-results" v-show="showResultsPanel">
      <div v-if="hasHistory" class="-clear" @click="clearHistory">
        <small class="wt2-link">Clear Recent</small>
      </div>
      <ul ref="results" :class="{ '-has-history': hasHistory }">
        <wt-highlighted-ac-result
          v-for="(i, key) in allSuggestions"
          @mouseenter.native="onHoverOnOption(key)"
          :class="{
            '-history': i.source === 'history',
            '-active': key === activeRowIndex,
          }"
          :key="i.id || i.text + '_' + key"
          :whole-text="i.text"
          :highlight="highlight"
          @mousedown.native="search({ text: i.text }, $event)"
        ></wt-highlighted-ac-result>
      </ul>
    </div>

    <transition name="wt2-fade-fast">
      <div v-show="tempValue.length" class="-clear" @click="clearTextBox">
        <div>
          <span class="wticon wticon-close_x"></span>
        </div>
      </div>
    </transition>
    <transition name="wt2-fade-fast">
      <div
        class="-search wt2-hover"
        :class="{ '-disabled': !canSearch }"
        @click="canSearch && search({ text: tempValue }, $event)"
      >
        <i class="wticon wticon-magnifier"></i>
      </div>
    </transition>
    <div class="-search-spacer"></div>
  </div>
</template>

<script>
import app from 'app';
import AutoCompleteMixins from './autoCompleteMixin.vue';

export default {
  mixins: [AutoCompleteMixins],

  data: function () {
    return {
      suggestionsFromServer: [],
      localHistoryFiltered: this.localHistory.slice(0, 5),
    };
  },

  props: {
    numHistoryItems: {
      type: Number,
      default: 3,
    },
    numSuggestions: {
      type: Number,
      default: 5,
    },
    acPromise: {
      type: Function,
      required: false,
      default: _.noop,
    },
    localHistory: {
      type: Array,
      default: () => [],
    },
    queryRequired: {
      type: Boolean,
      default: true,
    },
  },

  watch: {
    localHistory: {
      immediate: true,
      handler() {
        this.resetHistory();
      },
    },
    value(newVal) {
      if (!newVal) {
        this.suggestionsFromServer = [];
        this.resetHistory();
      }
      this.tempValue = newVal;
    },

    suggestionsFromServer(newVal) {
      this.$emit('response', newVal);
    },
  },
  computed: {
    query() {
      const value = this.value || '';
      return value.replace(/["']/g, '').trim();
    },
    inputClasses() {
      return {
        'wt-input': true,
        [this.inputClass]: true,
        '-with-button': !!this.button,
      };
    },
    allSuggestions() {
      let suggestions = this.suggestionsFromServer;

      suggestions = _.filter(suggestions, (s) => {
        const index = _.findIndex(this.localHistoryFiltered, { text: s.text });
        return index < 0;
      });

      return [...this.localHistoryFiltered, ...suggestions];
    },
    hasHistory() {
      return this.localHistoryFiltered.length > 0;
    },
    canSearch() {
      return !_.isEmpty(this.tempValue);
    },
  },
  beforeDestroy() {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.TLLB);
  },
  methods: {
    onInput(newVal) {
      this.tempValue = newVal;
      this.highlight = newVal;
      if (!this.disabled) {
        console.log(newVal);
        this.getSuggestionsFromServer(newVal);
        if (newVal) {
          this.localHistoryFiltered = this.filterSuggestions(
            this.localHistory,
            this.numHistoryItems,
            newVal
          );
        } else {
          this.localHistoryFiltered = this.localHistory.slice(0, 5);
        }
      }
    },
    onEnterPressed(e) {
      this.search({ text: this.tempValue }, e);
    },
    clearHistory() {
      this.localHistoryFiltered = [];
      this.$emit('clearHistory');
    },
    getSuggestionsFromServer: _.debounce(function (value) {
      value = (value || '').replace(/["']/g, '').trim();
      if (value) {
        const getPromise = this.acPromise(value);
        const p = getPromise.then((response) => {
          this.suggestionsFromServer = response.slice(0, +this.numSuggestions);
        });
        p.catch(() => (this.suggestionsFromServer = []));
      } else {
        this.suggestionsFromServer = [];
      }
    }, 350),
    refreshResults() {},
    search({ text }, e) {
      this.highlight = text;
      if (text) {
        this.tempValue = text;
        this.$emit('input', text);

        this.$emit('search', { e, activeRowIndex: this.activeRowIndex });
        if (!this.disabled) {
          this.$nextTick(() => {
            this.getSuggestionsFromServer(text);
            this.localHistoryFiltered = this.filterSuggestions(
              this.localHistory,
              this.numHistoryItems,
              text
            );
          });
        }
      } else {
        this.$emit('input', '');
        this.$emit('search', { e });
        this.suggestionsFromServer = [];
        this.resetHistory();
      }
      this.focused = false;
      this.activeRowIndex = null;
    },
    arrowPressed(offset) {
      if (this.disabled || !this.allSuggestions.length) {
        return;
      }
      if (this.activeRowIndex === null) {
        this.activeRowIndex = offset > 0 ? 0 : this.allSuggestions.length - 1;
      } else {
        this.activeRowIndex += offset;
      }
      if (this.activeRowIndex === this.allSuggestions.length) {
        this.activeRowIndex = 0;
      }
      if (this.activeRowIndex < 0) {
        this.activeRowIndex = this.allSuggestions.length - 1;
      }
      this.tempValue = this.allSuggestions[this.activeRowIndex].text;
      const el = this.$refs.results.childNodes[this.activeRowIndex];
      el.scrollIntoView({ block: 'center' });
    },
    resetHistory() {
      this.localHistoryFiltered = this.localHistory.slice(0, 5);
    },
  },
};
</script>
